import { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography } from "@mui/joy";
import { DataGrid } from "../../../components";
import { GfCActualValue, GfCMeasurementType } from "./GridFrameworkComponents";

const standardMeasurementData = [
    {
        name: "Area",
        total: "-",
        actual: "",
        unit: "sqft",
        description: "Total surface area of the roof",
    },
    {
        name: "Ridge",
        total: "-",
        actual: "",
        unit: "ft",
        description: "Horizontal peak line where two roof slopes meet",
    },
    {
        name: "Hip",
        total: "-",
        actual: "",
        unit: "ft",
        description: "Sloped edge where two roof sections meet",
    },
    {
        name: "Valley",
        total: "-",
        actual: "",
        unit: "ft",
        description: "Internal angle formed where two slopes meet",
    },
    {
        name: "Eave",
        total: "-",
        actual: "",
        unit: "ft",
        description: "Lower edge of the roof that overhangs the wall",
    },
    {
        name: "Rake",
        total: "-",
        actual: "",
        unit: "ft",
        description: "Sloped edge from eave to ridge on gable ends",
    },
    {
        name: "Step Flashing",
        total: "-",
        actual: "",
        unit: "ft",
        description: "Flashing used where roof meets a vertical wall",
    },
    {
        name: "Counter Flashing",
        total: "-",
        actual: "",
        unit: "ft",
        description: "Protective flashing installed over step flashing",
    },
];

type Measurement = {
    Id: string;
    ActualValue: number;
    [key: string]: any; // Extend as needed
};

export function RoofMeasurementTable(props: { measurements: any; setMeasurements?: any }) {
    const { measurements, setMeasurements } = props;
    const [data, setData] = useState(standardMeasurementData);

    const processedData = () => {
        if (measurements.length === 0) {
            return;
        }
        const newData = standardMeasurementData.map((item: any) => {
            const entry = measurements.find((entry: any) => entry.MeasurementType === item.name);
            if (!entry) {
                return item;
            }
            return {
                ...item,
                name: entry.MeasurementType,
                total: entry.CalculatedValue,
                actual: entry.ActualValue,
                id: entry.Id,
            };
        });

        setData(newData);
    };

    const handleEdit = (event: { id: string; actual: number }) => {
        const { id, actual } = event;
        setMeasurements((prev: Measurement[]) => prev.map((m) => (m.Id === id ? { ...m, ActualValue: actual } : m)));
    };

    useEffect(() => {
        processedData();
    }, [measurements]);

    return (
        <Box sx={{ width: "100%", overflow: "auto", mt: 1 }}>
            <Typography sx={{ fontWeight: 500, mb: 1 }} level="body-sm" gutterBottom>
                Roof Measurements
            </Typography>
            <Card sx={{ p: 0, overflow: "hidden" }}>
                <CardContent>
                    <DataGrid
                        gridData={data}
                        columnDefs={columnDefinitions}
                        autoHeight={true}
                        onEdit={handleEdit}
                        suppressQuickControl={true}
                    />
                </CardContent>
            </Card>
        </Box>
    );
}

const columnDefinitions = [
    { field: "name", headerName: "Measurement", flex: 1, cellRenderer: GfCMeasurementType },
    {
        field: "total",
        headerName: "Predicted",
        valueFormatter: (params: any) => {
            const value = Number(params.value) || 0;
            return `${value} ${params.data.unit}`;
        },
    },
    {
        field: "actual",
        headerName: "Actual",
        cellRenderer: GfCActualValue,
        cellStyle: { padding: 0 },
    },
    {
        field: "id",
        headerName: "Difference",
        valueGetter: (params: any) => {
            const total = Number(params.data.total) || 0;
            const actual = Number(params.data.actual) || 0;
            return total - actual;
        },
        valueFormatter: (params: any) => {
            const value = Number(params.value) || 0;
            return `${value} ${params.data.unit}`;
        },
        cellClassRules: {
            "diff-positive": (params: any) => params.value > 0,
            "diff-negative": (params: any) => params.value < 0,
            "diff-zero": (params: any) => params.value === 0,
        },
    },
];
